import { useCallback, useEffect, useMemo, useState } from "react";
import { AxiosResponse } from "axios";
import { detail } from "../../../../../services/table.services";
import { transactionDetail } from "../../../../../services/sales.service";
import { SOCKET_CLIENT } from "../../../../../../../utils/socket";
import { SOCKET_EVENTS } from "../../../../../../../utils/Constants";
import { calculateOrderPriceDetail } from "../utils/orderCalc.ts";
import { ORDER_TYPE } from "../utils/constants.ts";
import type { OrderItemAddon } from "../utils/splitCalc.ts";
import { distinctIdentities, getPaidQuantityForLine } from "./splitItemIdentity.ts";

interface OrderedItem {
  _id: string;
  quantity: number;
  order_id?: string;
  addons?: OrderItemAddon[];
  [key: string]: unknown;
}

export interface CombinedOrderItem extends OrderedItem {
  order_id: string;
}

interface PaidItem {
  menu_item_id: string;
  source_order_id: string;
  quantity: number;
  ordered_line_id?: string;
  // Line discriminators. Optional because rows written before they shipped
  // lack them, so matching may still need to fall back to menu/price/tax.
  price?: number;
  consumption_tax_rate?: number;
}

interface SubOrder {
  _id: string;
  ordered_items?: OrderedItem[];
  paid_items?: PaidItem[];
}

interface ActiveOrder {
  _id: string;
  ordered_items?: OrderedItem[];
  subOrders?: SubOrder[];
  paid_items?: PaidItem[];
  service_charge_percentage?: number;
  [key: string]: unknown;
}

export interface TableDetail {
  activeOrder?: ActiveOrder;
  is_available?: boolean;
  [key: string]: unknown;
}

interface TransactionData {
  orderDetail?: ActiveOrder;
  coupon_applied?: boolean;
  [key: string]: unknown;
}

type OrderType = "new-order" | "edit-sales" | string;

interface UseOrderDataProps {
  tableId: string | undefined | null;
  orderType: OrderType;
  taxRoundType?: string;
}

interface UseOrderDataReturn {
  tableDetail: TableDetail;
  orderItems: CombinedOrderItem[];
  orderPriceDetail: ReturnType<typeof calculateOrderPriceDetail> | null;
  availableItems: CombinedOrderItem[];
  transactionData: TransactionData;
  activeOrder: ActiveOrder | undefined;
  isOrderValid: boolean;
  refresh: number;
  reloadOrderData: () => void;
}

/**
 * `includeSubOrders: false` for a settled order (the edit-sales path). At
 * settlement the base order's `ordered_items` is overwritten with the flattened
 * union of base + sub-order lines (payByCash controller.js:467,
 * split-payment settlement.ts:56) while the sub-orders keep their own copies —
 * so folding `subOrders` back in shows every sub-order line twice.
 */
const buildCombinedOrderItems = (
  primaryOrder: ActiveOrder | undefined,
  { includeSubOrders = true }: { includeSubOrders?: boolean } = {},
): CombinedOrderItem[] => {
  const combinedItems: CombinedOrderItem[] = (primaryOrder?.ordered_items || []).map((item) => ({
    ...item,
    order_id: primaryOrder?._id ?? "",
  }));

  if (!includeSubOrders) {
    return combinedItems;
  }

  (primaryOrder?.subOrders || []).forEach((subOrder) => {
    const orderId = subOrder._id;
    const updatedItems: CombinedOrderItem[] = (subOrder?.ordered_items || []).map((item) => ({
      ...item,
      order_id: orderId,
    }));
    combinedItems.push(...updatedItems);
  });

  return combinedItems;
};

export const buildAvailableItems = (
  combinedItems: CombinedOrderItem[] = [],
  activeOrder: ActiveOrder = {} as ActiveOrder,
): CombinedOrderItem[] => {
  const paidItems: PaidItem[] = [...(activeOrder?.paid_items || [])];
  (activeOrder?.subOrders || []).forEach((subOrder) => {
    if (subOrder.paid_items?.length) {
      paidItems.push(...subOrder.paid_items);
    }
  });

  // Every line's identity, so a legacy paid row lacking ordered_line_id can
  // still be resolved unambiguously instead of pooling onto every line that
  // shares its menu item/price/tax.
  const knownIdentities = distinctIdentities(combinedItems);

  return combinedItems
    .map((item): CombinedOrderItem | null => {
      const paidQty = getPaidQuantityForLine(item, paidItems, knownIdentities);
      const remainingQty = Number(item.quantity || 0) - paidQty;
      return remainingQty > 0 ? { ...item, quantity: remainingQty } : null;
    })
    .filter((item): item is CombinedOrderItem => item !== null);
};

export const useOrderData = ({
  tableId,
  orderType,
  // The restaurant's tax rounding mode — the post-discount bracket subtotals
  // derive their tax from the bracket totals and must round it as the backend does.
  taxRoundType,
}: UseOrderDataProps): UseOrderDataReturn => {
  const [tableDetail, setTableDetail] = useState<TableDetail>({});
  const [orderItems, setOrderItems] = useState<CombinedOrderItem[]>([]);
  const [availableItems, setAvailableItems] = useState<CombinedOrderItem[]>([]);
  const [orderPriceDetail, setOrderPriceDetail] = useState<ReturnType<typeof calculateOrderPriceDetail> | null>(null);
  const [transactionData, setTransactionData] = useState<TransactionData>({});
  const [isOrderValid, setIsOrderValid] = useState<boolean>(false);
  const [refresh, setRefresh] = useState<number>(0);

  const activeOrder = useMemo<ActiveOrder | undefined>(
    () => (orderType === ORDER_TYPE.EDIT_SALES ? transactionData?.orderDetail : tableDetail?.activeOrder),
    [orderType, tableDetail, transactionData],
  );

  const reloadOrderData = useCallback((): void => {
    setRefresh((prev) => prev + 1);
  }, []);

  const loadOrderData = useCallback(async (): Promise<void> => {
    if (!tableId || !orderType) return;

    if (orderType === ORDER_TYPE.EDIT_SALES) {
      try {
        const { data } = await transactionDetail({ o_id: tableId }) as AxiosResponse<TransactionData>;
        const valid = !!data;
        setIsOrderValid(valid);

        const nextTransactionData: TransactionData = data || {};
        const nextOrderDetail: ActiveOrder = nextTransactionData?.orderDetail || ({} as ActiveOrder);
        const combinedItems = buildCombinedOrderItems(nextOrderDetail, {
          includeSubOrders: false,
        });

        setTransactionData(nextTransactionData);
        setOrderItems(combinedItems);
        setAvailableItems(combinedItems);
        setOrderPriceDetail(calculateOrderPriceDetail(nextOrderDetail, data?.coupon_applied, taxRoundType));
      } catch (error: unknown) {
        console.error("[useOrderData] transactionDetail failed", error);
      }

      return;
    }

    try {
      const { data } = await detail({ o_id: tableId }) as AxiosResponse<TableDetail & { coupon_code_applied?: boolean }>;
      const nextTableDetail: TableDetail = data || {};
      const nextActiveOrder: ActiveOrder = nextTableDetail?.activeOrder || ({} as ActiveOrder);

      setTableDetail(nextTableDetail);

      const valid = !!nextTableDetail?.activeOrder;
      setIsOrderValid(valid);

      if (orderType === ORDER_TYPE.NEW_ORDER && !nextTableDetail?.activeOrder) {
        setOrderItems([]);
        setAvailableItems([]);
        setOrderPriceDetail(calculateOrderPriceDetail({}, false, taxRoundType));
        return;
      }

      if (nextTableDetail?.is_available === false) {
        const combinedItems = buildCombinedOrderItems(nextActiveOrder);

        setOrderItems(combinedItems);
        setAvailableItems(buildAvailableItems(combinedItems, nextActiveOrder));
        setOrderPriceDetail(calculateOrderPriceDetail(nextActiveOrder, data?.coupon_code_applied, taxRoundType));
        return;
      }

      setOrderItems([]);
      setAvailableItems([]);
      setOrderPriceDetail(calculateOrderPriceDetail({}, false, taxRoundType));
    } catch (error: unknown) {
      console.error("[useOrderData] detail failed", error);
    }
  }, [orderType, tableId, taxRoundType]);

  useEffect(() => {
    void loadOrderData();
  }, [loadOrderData, refresh]);

  useEffect(() => {
    localStorage.setItem("is_valid_order", String(isOrderValid));
  }, [isOrderValid]);

  useEffect(() => {
    localStorage.setItem(
      "has_split_payment",
      String(Boolean(activeOrder && "has_split_payment" in activeOrder ? activeOrder.has_split_payment : false)),
    );
  }, [activeOrder]);

  useEffect(() => {
    const handleSocketData = (response: { event: string }): void => {
      if (
        response.event === SOCKET_EVENTS.RESTAURANT_DETAILS_CHANGED &&
        tableId &&
        orderType !== ORDER_TYPE.NEW_ORDER
      ) {
        reloadOrderData();
      }
    };

    SOCKET_CLIENT.on("GET-DATA", handleSocketData);

    return () => {
      SOCKET_CLIENT.off("GET-DATA", handleSocketData);
    };
  }, [tableId, orderType, reloadOrderData]);

  return {
    tableDetail,
    orderItems,
    orderPriceDetail,
    availableItems,
    transactionData,
    activeOrder,
    isOrderValid,
    refresh,
    reloadOrderData,
  };
};
